'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import SafeLink from '@/components/common/SafeLink';
import { ShoppingCart } from 'lucide-react';
import { addToCart } from '@lib/data/cart';
import { useCart } from '@lib/hooks/useCart';

interface RecommendedProduct {
  id: string;
  title: string;
  handle: string;
  thumbnail?: string | null;
  variants?: { id: string; calculated_price?: { calculated_amount: number }; title?: string }[];
}

interface CartUpsellWidgetProps {
  countryCode?: string;
  regionId?: string;
  onClose?: () => void;
}

export default function CartUpsellWidget({ countryCode: countryCodeProp, regionId, onClose }: CartUpsellWidgetProps) {
  const { cart } = useCart();
  const params = useParams();
  // Prefer prop, then URL param, then fallback
  const countryCode = countryCodeProp ?? (params?.countryCode as string) ?? 'us';
  const [products, setProducts] = useState<RecommendedProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);

  // Pick the product_id of the highest-value cart item
  const sourceProductId = React.useMemo(() => {
    if (!cart?.items?.length) return null;
    const sorted = [...cart.items].sort(
      (a, b) => (b.unit_price * b.quantity) - (a.unit_price * a.quantity)
    );
    return (sorted[0] as any)?.variant?.product_id ?? null;
  }, [cart?.items]);

  const cartProductIds = React.useMemo(
    () => new Set(cart?.items?.map((i: any) => i.variant?.product_id).filter(Boolean) ?? []),
    [cart?.items]
  );

  useEffect(() => {
    if (!sourceProductId) return;

    setLoading(true);
    const medusaUrl = process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL || '';
    const params = new URLSearchParams({ type: 'crosssell', limit: '4' });
    if (regionId) params.set('region_id', regionId);

    fetch(`${medusaUrl}/store/products/${sourceProductId}/recommendations?${params}`)
      .then((r) => r.ok ? r.json() : { recommendations: [] })
      .then((data) => {
        // Filter out products already in the cart
        const filtered = (data.recommendations ?? []).filter(
          (p: RecommendedProduct) => !cartProductIds.has(p.id)
        );
        setProducts(filtered.slice(0, 4));
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [sourceProductId, regionId, cartProductIds]);

  const handleAdd = async (product: RecommendedProduct) => {
    const variant = product.variants?.[0];
    if (!variant) return;
    setAddingId(product.id);
    try {
      await addToCart({ variantId: variant.id, quantity: 1, countryCode });
    } catch {
      // ignore
    } finally {
      setAddingId(null);
    }
  };

  if (loading || products.length === 0) return null;

  const formatPrice = (product: RecommendedProduct) => {
    const amount = product.variants?.[0]?.calculated_price?.calculated_amount;
    if (!amount) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: cart?.currency_code?.toUpperCase() ?? 'USD',
    }).format(amount);
  };

  return (
    <div className="border-t border-gray-100 px-5 py-4">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
        You might also like
      </p>
      <div className="space-y-2">
        {products.map((product) => (
          <div
            key={product.id}
            className="flex items-center gap-3 rounded-lg border border-gray-100 p-2 hover:border-gray-200 transition-colors"
          >
            {/* Thumbnail */}
            <SafeLink href={`/products/${product.handle}`} onClick={onClose} className="shrink-0">
              <div className="w-12 h-12 rounded bg-gray-50 overflow-hidden relative">
                {product.thumbnail ? (
                  <Image
                    src={product.thumbnail}
                    alt={product.title}
                    fill
                    className="object-cover"
                    sizes="48px"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300">
                    <ShoppingCart className="w-5 h-5" />
                  </div>
                )}
              </div>
            </SafeLink>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <SafeLink href={`/products/${product.handle}`} onClick={onClose}>
                <p className="text-sm font-medium text-gray-900 truncate leading-tight">
                  {product.title}
                </p>
              </SafeLink>
              {formatPrice(product) && (
                <p className="text-xs text-gray-500 mt-0.5">{formatPrice(product)}</p>
              )}
            </div>

            {/* Add button */}
            <button
              onClick={() => handleAdd(product)}
              disabled={addingId === product.id}
              className="shrink-0 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {addingId === product.id ? '...' : '+ Add'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
